/** 站点本位币 */
export type BaseCurrency = 'CNY' | 'USDT'

export function isBaseCurrency(v: unknown): v is BaseCurrency {
  return v === 'CNY' || v === 'USDT'
}

/** 解析展示金额字符串 → 数值 */
export function parseMoney(s: string | number): number {
  if (typeof s === 'number') return Number.isFinite(s) ? s : 0
  return parseFloat(String(s).replace(/,/g, '')) || 0
}

/** 两位小数四舍五入 */
export function roundMoney2(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * 从「账本最小单位 + 展示串」推断放大倍数。
 * 例：min_amount=10000000 且 min_amount_display=10.00 → 1_000_000
 */
export const DEFAULT_MINOR_SCALE = 1_000_000

export function inferMinorScale(minor: number, display: string): number {
  const n = parseMoney(display)
  if (n > 0 && minor > 0) {
    const scale = Math.round(minor / n)
    if (scale > 0) return scale
  }
  return DEFAULT_MINOR_SCALE
}

/**
 * 展示金额 → 账本最小单位字符串（额度转换 amount 字段）。
 * 小数有几位就按几位换，不四舍五入、也不截成两位。
 * 浮点 `2.01 * 1e6` 是 2009999.9999999998，不能直接乘。
 * 1.005 × 1e6 = 1005000。
 */
export function displayToMinorUnits(display: number | string, scale: number): string {
  if (!Number.isFinite(scale) || scale <= 0) return '0'
  const cleaned = String(display).replace(/,/g, '').trim()
  const match = cleaned.match(/^(-?)(\d+)(?:\.(\d*))?$/)
  if (!match) return '0'
  const negative = match[1] === '-'
  const intPart = (match[2] ?? '0').replace(/^0+(?=\d)/, '') || '0'
  const frac = match[3] ?? ''
  const denom = 10n ** BigInt(frac.length)
  const num = BigInt(intPart) * denom + BigInt(frac || '0')
  const minor = (num * BigInt(Math.trunc(scale))) / denom
  const text = minor.toString()
  return negative && minor !== 0n ? `-${text}` : text
}

/** 去掉小数末尾的 0。1.0050 → 1.005，5.0000 → 5 */
function formatExactDecimal(numerator: bigint, decimals: number): string {
  if (numerator <= 0n) return '0'
  const base = 10n ** BigInt(decimals)
  const whole = numerator / base
  const frac = (numerator % base).toString().padStart(decimals, '0').replace(/0+$/, '')
  return frac ? `${whole.toString()}.${frac}` : whole.toString()
}

/**
 * 余额 × 百分比，精确到计算结果，不四舍五入、不截断。
 * 2.01 的 50% 是 1.005。
 */
export function moneyPercent(display: number, pct: number): string {
  if (!Number.isFinite(display) || !Number.isFinite(pct) || pct <= 0 || display <= 0) return '0'
  const cents = Math.round(display * 100)
  if (!Number.isSafeInteger(cents) || cents <= 0) return '0'
  return formatExactDecimal(BigInt(cents) * BigInt(Math.trunc(pct)), 4)
}

/**
 * 余额展示：原样输出 available_display 的小数位，不四舍五入、不截断。
 * 仅给整数部分加千分位，小数部分与后端返回一致（含末尾 0）。
 */
export function formatAvailableDisplay(raw: string | number | null | undefined): string {
  const cleaned = String(raw ?? '')
    .replace(/,/g, '')
    .trim()
  if (!cleaned) return '0'
  const match = cleaned.match(/^(-?)(\d+)(?:\.(\d*))?$/)
  if (!match) return cleaned
  const sign = match[1]
  const intPart = (match[2] ?? '0').replace(/^0+(?=\d)/, '') || '0'
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const frac = match[3]
  return frac != null && frac.length > 0 ? `${sign}${grouped}.${frac}` : `${sign}${grouped}`
}

/** 数值 → 千分位两位小数（不含币种符号） */
export function formatMoneyPlain(n: number): string {
  return roundMoney2(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** 本位币金额展示：CNY 前缀 ¥ / USDT 后缀 */
export function formatBaseAmt(n: number, currency: BaseCurrency): string {
  const f = formatMoneyPlain(n)
  return currency === 'USDT' ? `${f} USDT` : `¥${f}`
}

/**
 * 爆奖/中奖流展示（整数千分位，不强制小数）
 * CNY → +¥3,900 · USDT → +3,900 USDT
 */
export function formatJackpotAmt(n: number, currency: BaseCurrency): string {
  const f = Math.round(n).toLocaleString('en-US')
  return currency === 'USDT' ? `+${f} USDT` : `+¥${f}`
}

/** 币种图标路径 */
export function currencyIconPath(currency: BaseCurrency): string {
  return currency === 'USDT' ? '/img/pay/usdt.svg' : '/img/pay/cny.svg'
}

/** 余额旁短标签：CNY → ¥ · USDT → USDT */
export function currencyCodeLabel(currency: BaseCurrency): string {
  return currency === 'USDT' ? 'USDT' : '¥'
}
